// Improve Newco's own Google Analytics 4 in the app, through our Tag Manager
// container (the website's), and only:
//   * after analytics consent (utils/consent.js, shared with the website);
//   * on the public pages: sign up, sign in, the password pages, thank you;
//   * while nobody is signed in.
// What may be measured inside the signed in app is decision D4 in the plan,
// still open, so nothing is. If Google's tag is running when someone signs
// in, the page reloads once without it (onRouteChange).
//
// Page views carry the route TEMPLATE (/#/sso/:userId?/...), never the real
// path or any query, because app addresses carry emails, tokens and ids.
// Events are plain dataLayer pushes, sent by the container's "GA4 events"
// tag: a Google tag loaded by Tag Manager ignores gtag('event') calls. The
// dataLayer keys must match SITE_EVENT_PARAMETERS in scripts/google-api.js
// in the tracker repo. Staging builds load the container's Staging
// environment (VUE_APP_GTM_ENV_AUTH, VUE_APP_GTM_ENV_PREVIEW), which sends to
// the staging property with cookies prefixed "stg".
//
// Plan: docs/plans/analytics-newco-replacement-2026-10.md in the tracker repo.

import { hasAnalytics } from "@/utils/consent";
import { IMPROVE_CONFIG } from "@/config.js";

const GTM_ID = process.env.VUE_APP_GTM_ID || "";
const GTM_ENV_AUTH = process.env.VUE_APP_GTM_ENV_AUTH || "";
const GTM_ENV_PREVIEW = process.env.VUE_APP_GTM_ENV_PREVIEW || "";
const SITE = IMPROVE_CONFIG.APP_NAME === "partner" ? "partner_app" : "direct_app";
// Event keys are cleared on every push, so no event inherits another's. The
// page keys stay until the next page view.
const EVENT_KEYS = ["form", "method", "slot", "payment_type"];
// _ga and _ga_<stream>; on staging stg_ga and stg_ga_<stream>.
const GA_COOKIE = /^([A-Za-z0-9]+)?_ga(_[A-Za-z0-9]+)?$/;
const SHARED_DOMAIN = "improve.online";

let loaded = false;
let lastLocation = "";

function gtag() {
  // Consent commands only. Tag Manager reads the arguments object itself, so
  // this stays a plain function.
  window.dataLayer.push(arguments);
}

export function isAnalyticsLoaded() {
  return loaded;
}

const isPublic = (route) => route.matched.length > 0 && route.matched.every((r) => r.meta && r.meta.public);

// May this page be measured right now?
export function mayMeasure(route, signedIn) {
  return !!GTM_ID && !signedIn && isPublic(route) && hasAnalytics();
}

// The address Google may see: the route template, no query.
function routeLocation(route) {
  const record = route.matched[route.matched.length - 1];
  return window.location.origin + "/#" + record.path;
}

// Another site's address may carry its own personal data in the query.
function sanitisedReferrer(referrer) {
  if (!referrer) return "";
  try {
    const url = new URL(referrer);
    return url.origin + url.pathname;
  } catch (e) {
    return "";
  }
}

// A visitor the website hands over (Start your free trial) is the same visit:
// our own sites must not be credited as its source, or the campaign that
// brought the visitor is lost.
function isOwnSite(referrer) {
  try {
    const host = new URL(referrer).hostname;
    return host === SHARED_DOMAIN || host.endsWith("." + SHARED_DOMAIN);
  } catch (e) {
    return false;
  }
}

function loadAnalytics() {
  if (loaded || !GTM_ID || !hasAnalytics()) return;
  loaded = true;
  window.dataLayer = window.dataLayer || [];
  // Consent Mode v2, before the container: everything denied by default, then
  // only analytics granted. The ads signals stay denied: we run no advertising.
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "granted",
  });
  gtag("consent", "update", { analytics_storage: "granted" });
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

  const env = GTM_ENV_AUTH
    ? "&gtm_auth=" + encodeURIComponent(GTM_ENV_AUTH) +
      "&gtm_preview=" + encodeURIComponent(GTM_ENV_PREVIEW) + "&gtm_cookies_win=x"
    : "";
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://www.googletagmanager.com/gtm.js?id=" + encodeURIComponent(GTM_ID) + env;
  document.head.appendChild(script);
}

// Sends one GA4 event when the visitor allowed analytics. Callers are on the
// public pages only. Never put a name, an email, a company or an id that
// unlocks anything in `params`.
export function track(name, params = {}) {
  if (!GTM_ID || !hasAnalytics()) return;
  loadAnalytics();
  const event = { event: name, site: SITE, ecommerce: null };
  EVENT_KEYS.forEach((key) => {
    event[key] = undefined;
  });
  window.dataLayer.push(Object.assign(event, params));
}

function trackPageView(route) {
  const location = routeLocation(route);
  const referrer = lastLocation || sanitisedReferrer(document.referrer);
  lastLocation = location;
  track("page_view", {
    page_location: location,
    page_referrer: referrer,
    page_title: route.name || document.title,
    ignore_referrer: isOwnSite(referrer) || undefined,
  });
}

// Refusal or withdrawal: tell the Google tag, then delete Google's cookies on
// the shared domain and on this host.
function clearAnalytics() {
  if (loaded) gtag("consent", "update", { analytics_storage: "denied" });
  document.cookie
    .split(";")
    .map((c) => c.split("=")[0].trim())
    .filter((name) => GA_COOKIE.test(name))
    .forEach((name) => {
      document.cookie = name + "=; max-age=0; path=/";
      document.cookie = name + "=; max-age=0; path=/; domain=." + SHARED_DOMAIN;
    });
}

// After every navigation (router.js).
export function onRouteChange(route, signedIn) {
  if (mayMeasure(route, signedIn)) {
    trackPageView(route);
  } else if (loaded) {
    // Signed in with Google's tag running: reload once, so it runs on no
    // signed in page. Nothing loads it again while signed in.
    window.location.reload();
  }
}

// After a live choice in the cookie banner, on the page the visitor is on.
// setConsent() must be called first.
export function onConsentChange(route, signedIn) {
  if (mayMeasure(route, signedIn)) {
    if (!loaded) trackPageView(route);
    return;
  }
  const wasRunning = loaded;
  clearAnalytics();
  // A single page app never unloads the tag on its own; the stored choice
  // keeps it off after the reload.
  if (wasRunning) window.location.reload();
}
